import { Button } from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { AppIcon } from '../../components/icons/AppIcon';
import AuthBrandPanel from '../auth/components/AuthBrandPanel';
import { PasswordStrengthMeter } from '../auth/components/PasswordStrengthMeter';
import { cn } from '@/lib/utils';
import { DEFAULT_COMMISSION_RATE } from '../../domain/partners/types';
import { usePartnerPublicApply } from './hooks/usePartnerPublicApply';

const COMMISSION_PCT = Math.round(DEFAULT_COMMISSION_RATE * 100);

const PARTNER_TAGS = [
  `${COMMISSION_PCT}% recorrente`,
  'Link exclusivo',
  'Payout Stripe',
] as const;

export function PartnerPublicApplyPage() {
  const {
    fullName,
    email,
    phone,
    password,
    confirmPassword,
    companyName,
    cnpj,
    fieldErrors,
    formError,
    submitting,
    setFullName,
    setEmail,
    setPhone,
    setPassword,
    setConfirmPassword,
    setCompanyName,
    setCnpj,
    submit,
    goToLogin,
  } = usePartnerPublicApply();

  const showPasswordMatch = confirmPassword.length > 0;
  const passwordsMatch = showPasswordMatch && password === confirmPassword;

  return (
    <main className="flex min-h-dvh flex-col bg-background px-s4 py-s6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-s6 md:px-s8 md:py-s10">
      <article className="mx-auto my-auto flex w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-subtle bg-card shadow-sm md:rounded-2xl md:shadow-md lg:flex-row">
        <section
          className="flex flex-1 flex-col p-s6 sm:p-s8 lg:p-s10"
          aria-labelledby="partner-public-apply-title"
        >
          <div className="mx-auto w-full max-w-md">
            <div className="mb-s6 flex justify-center lg:hidden">
              <img
                src="/logo%20principal%20140x40.png"
                alt="Adlitics"
                className="h-8 w-auto object-contain dark:hidden"
              />
              <img
                src="/logo%20principal%20dark%20140x40.png"
                alt="Adlitics"
                className="hidden h-8 w-auto object-contain dark:block"
              />
            </div>

            <header className="mb-s6 flex flex-col gap-s2 text-center lg:text-left">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                Programa de parceiros
              </p>
              <h1
                id="partner-public-apply-title"
                className="text-2xl text-default md:text-3xl"
              >
                Cadastre sua agência
              </h1>
              <p className="text-sm text-secondary">
                Crie sua conta e envie a candidatura. Após aprovação, você recebe o link de
                indicação e acompanha as comissões.
              </p>
            </header>

            {formError && (
              <div
                role="alert"
                className="mb-s4 flex items-start gap-s3 rounded-lg border border-error/30 bg-danger-bg px-s4 py-s3 text-left"
              >
                <AppIcon name="alert" className="mt-0.5 size-4 shrink-0 text-danger-text" />
                <p className="text-sm font-medium text-danger-text">{formError}</p>
              </div>
            )}

            <form onSubmit={submit} className="flex flex-col gap-s4" noValidate aria-busy={submitting}>
              <fieldset className="flex flex-col gap-s4 border-0 p-0">
                <legend className="mb-s1 text-sm font-semibold text-default">Sua conta</legend>

                <Input
                  label="Nome completo"
                  id="partner-full-name"
                  name="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  required
                  error={fieldErrors.fullName}
                  inputSize="lg"
                  autoFocus
                />

                <Input
                  label="E-mail"
                  id="partner-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  error={fieldErrors.email}
                  inputSize="lg"
                />

                <Input
                  label="Telefone (WhatsApp)"
                  id="partner-phone"
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  helperText="Com DDD, ex.: +55 (11) 91234-5678"
                  required
                  error={fieldErrors.phone}
                  inputSize="lg"
                  className="font-mono"
                />

                <div>
                  <Input
                    label="Senha"
                    id="partner-password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    error={fieldErrors.password}
                    inputSize="lg"
                  />
                  <PasswordStrengthMeter password={password} />
                </div>

                <div>
                  <Input
                    label="Confirmar senha"
                    id="partner-confirm-password"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    error={fieldErrors.confirmPassword}
                    inputSize="lg"
                  />
                  {showPasswordMatch && (
                    <p
                      className={cn(
                        'mt-s2 text-xs font-medium',
                        passwordsMatch ? 'text-success-text' : 'text-danger-text',
                      )}
                      aria-live="polite"
                    >
                      {passwordsMatch ? 'Senhas coincidem ✓' : 'Senhas não coincidem'}
                    </p>
                  )}
                </div>
              </fieldset>

              <fieldset className="flex flex-col gap-s4 border-0 border-t border-subtle pt-s4 p-0">
                <legend className="mb-s1 text-sm font-semibold text-default">Agência</legend>

                <Input
                  label="Nome da agência"
                  id="partner-company-name"
                  name="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  autoComplete="organization"
                  required
                  error={fieldErrors.companyName}
                  inputSize="lg"
                />

                <Input
                  label="CNPJ"
                  id="partner-cnpj"
                  name="cnpj"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  inputMode="numeric"
                  autoComplete="off"
                  helperText="Opcional — formato 00.000.000/0000-00"
                  error={fieldErrors.cnpj}
                  inputSize="lg"
                  className="font-mono tabular-nums"
                />
              </fieldset>

              <Button
                type="submit"
                size="lg"
                className="mt-s2 min-h-11 w-full touch-manipulation"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="mr-s2" />
                    Criando conta…
                  </>
                ) : (
                  'Criar conta e candidatar'
                )}
              </Button>
            </form>

            <p className="mt-s5 text-center text-sm text-secondary">
              Já tem uma conta?{' '}
              <button
                type="button"
                onClick={goToLogin}
                className="font-medium text-primary transition-colors hover:text-primary-dark"
              >
                Entrar e candidatar
              </button>
            </p>
          </div>
        </section>

        <AuthBrandPanel
          tagline="Indique clientes e ganhe"
          taglineHighlight="comissão recorrente."
          description={`Parceiros Adlitics recebem ${COMMISSION_PCT}% sobre assinaturas indicadas, com link exclusivo e payout via Stripe.`}
          featureTags={PARTNER_TAGS}
        />
      </article>

      <footer className="mx-auto mt-s6 w-full max-w-md px-s4 text-center md:mt-s8">
        <p className="text-sm text-muted">
          Ao continuar, você concorda com nossos{' '}
          <a
            href="https://adlitics.com.br/termos"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Termos de Serviço
          </a>{' '}
          e{' '}
          <a
            href="https://adlitics.com.br/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Política de Privacidade
          </a>
          .
        </p>
      </footer>
    </main>
  );
}
